import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  MessageSquare, 
  Send,
  Search,
  User,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

function MessagesPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialUserId = searchParams.get('user');

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const loadConversations = useCallback(async () => {
    setConversationsLoading(true);
    try {
      const response = await axios.get('/messages/conversations');
      setConversations(response.data || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      toast.error('Failed to load conversations');
      setConversations([]);
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  const bootstrapConversationFromUser = useCallback(async (targetUserId) => {
    try {
      const response = await axios.get(`/bartenders/${targetUserId}`);
      const participantUser = response.data?.user;
      if (!participantUser?.id) {
        throw new Error('Participant not found');
      }

      const adHocConversation = {
        id: participantUser.id,
        participant: {
          id: participantUser.id,
          first_name: participantUser.first_name || '',
          last_name: participantUser.last_name || '',
          profile_picture: participantUser.profile_picture || null,
          user_type: participantUser.user_type || 'bartender',
        },
        last_message: {
          content: '',
          timestamp: null,
          sender_id: null,
        },
        unread_count: 0,
      };

      setConversations((prev) => {
        const exists = prev.some((item) => item.participant?.id === participantUser.id);
        if (exists) return prev;
        return [adHocConversation, ...prev];
      });
      setSelectedConversation(adHocConversation);

      const conversationMessages = await axios.get(`/messages/${participantUser.id}`);
      setMessages(conversationMessages.data || []);
    } catch (error) {
      console.error('Failed to prepare conversation from profile link:', error);
      toast.error('Unable to open this conversation yet');
    }
  }, []);

  const handleConversationSelect = useCallback(async (conversation) => {
    setSelectedConversation(conversation);
    setLoading(true);
    
    try {
      const response = await axios.get(`/messages/${conversation.participant.id}`);
      setMessages(response.data || []);

      // mark current conversation unread badge as consumed
      setConversations((prev) => prev.map((item) => {
        if (item.participant?.id !== conversation.participant?.id) return item;
        return { ...item, unread_count: 0 };
      }));
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (!initialUserId) return;

    const existingConversation = conversations.find((conversation) => conversation.participant?.id === initialUserId);
    if (existingConversation) {
      handleConversationSelect(existingConversation);
      return;
    }

    bootstrapConversationFromUser(initialUserId);
  }, [initialUserId, conversations, handleConversationSelect, bootstrapConversationFromUser]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const messageData = {
        receiver_id: selectedConversation.participant.id,
        content: newMessage.trim()
      };

      const response = await axios.post('/messages', messageData);
      const newMsg = response.data;

      setMessages(prev => [...prev, newMsg]);
      setNewMessage('');

      // Update last message preview + move conversation to top
      setConversations((prev) => {
        const updated = prev.map((conversation) => {
          if (conversation.participant?.id !== selectedConversation.participant?.id) {
            return conversation;
          }

          return {
            ...conversation,
            last_message: {
              content: newMsg.content,
              timestamp: newMsg.created_at,
              sender_id: newMsg.sender_id,
            },
          };
        });

        updated.sort((a, b) => new Date(b.last_message?.timestamp || 0) - new Date(a.last_message?.timestamp || 0));
        return updated;
      });

      toast.success('Message sent!');
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateValue) => {
    if (!dateValue) return 'Now';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return 'Now';

    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filteredConversations = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return conversations;

    return conversations.filter((conv) => {
      const firstName = (conv.participant?.first_name || '').toLowerCase();
      const lastName = (conv.participant?.last_name || '').toLowerCase();
      const fullName = `${firstName} ${lastName}`.trim();
      return firstName.includes(normalizedSearch) || lastName.includes(normalizedSearch) || fullName.includes(normalizedSearch);
    });
  }, [conversations, searchTerm]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="messages-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-2 font-['Barlow_Condensed']">
          Messages
        </h1>
        <p className="text-lg text-muted-foreground">
          Connect with bar owners and bartenders directly
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[600px]">
        {/* Conversations List */}
        <Card className="bg-white/5 backdrop-blur-md border border-white/5">
          <CardHeader className="pb-3">
            <CardTitle className="font-['Barlow_Condensed'] uppercase text-sm">Conversations</CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-9"
                data-testid="search-conversations-input"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[480px] overflow-y-auto">
              {conversationsLoading ? (
                <div className="p-6 text-center" data-testid="conversations-loading-state">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary mx-auto mb-4" />
                  <p className="text-muted-foreground text-sm">Loading conversations...</p>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="p-6 text-center">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground text-sm">
                    {searchTerm ? 'No conversations found' : 'No messages yet'}
                  </p>
                </div>
              ) : (
                filteredConversations.map((conversation) => (
                  <div 
                    key={conversation.id}
                    className={`p-4 border-b border-white/10 cursor-pointer hover:bg-white/5 transition-colors ${
                      selectedConversation?.id === conversation.id ? 'bg-white/10' : ''
                    }`}
                    onClick={() => handleConversationSelect(conversation)}
                    data-testid={`conversation-${conversation.id}`}
                  >
                    <div className="flex items-start space-x-3">
                      <Avatar className="h-10 w-10 flex-shrink-0">
                        <AvatarImage 
                          src={conversation.participant.profile_picture} 
                          alt={`${conversation.participant.first_name} ${conversation.participant.last_name}`}
                        />
                        <AvatarFallback className="bg-primary/10 text-primary text-sm">
                          {conversation.participant.first_name[0]}{conversation.participant.last_name[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="text-sm font-medium text-white truncate">
                            {conversation.participant.first_name} {conversation.participant.last_name}
                          </h3>
                          <div className="flex items-center space-x-2">
                          {Number(conversation.unread_count || 0) > 0 && (
                              <Badge variant="default" className="h-5 w-5 p-0 flex items-center justify-center text-xs">
                                {conversation.unread_count}
                              </Badge>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {formatTime(conversation.last_message.timestamp)}
                            </span>
                          </div>
                        </div>
                        <Badge variant="outline" className="mb-1 text-xs">
                          {conversation.participant.user_type === 'bar_owner' ? 'Bar Owner' : 'Bartender'}
                        </Badge>
                        <p className="text-sm text-muted-foreground truncate">
                          {conversation.last_message?.sender_id === user.id ? 'You: ' : ''}
                          {conversation.last_message?.content || 'No messages yet'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Chat Area */}
        <div className="lg:col-span-2">
          {!selectedConversation ? (
            <Card className="bg-white/5 backdrop-blur-md border border-white/5 h-full">
              <CardContent className="h-full flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">Select a conversation</h3>
                  <p className="text-muted-foreground">
                    Choose a conversation from the left to start messaging
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white/5 backdrop-blur-md border border-white/5 h-full flex flex-col">
              {/* Chat Header */}
              <CardHeader className="pb-3 border-b border-white/10">
                <div className="flex items-center space-x-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage 
                      src={selectedConversation.participant.profile_picture} 
                      alt={`${selectedConversation.participant.first_name} ${selectedConversation.participant.last_name}`}
                    />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {(selectedConversation.participant.first_name || 'U')[0]}{(selectedConversation.participant.last_name || '')[0] || ''}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-white">
                      {selectedConversation.participant.first_name} {selectedConversation.participant.last_name}
                    </h3>
                    <Badge variant="outline" className="text-xs">
                      {selectedConversation.participant.user_type === 'bar_owner' ? 'Bar Owner' : 'Bartender'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>

              {/* Messages */}
              <div className="flex-1 p-4 overflow-y-auto space-y-4 min-h-0" data-testid="messages-container">
                {loading ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex items-center justify-center h-32" data-testid="empty-conversation-state">
                    <p className="text-muted-foreground text-sm">No messages yet. Start the conversation below.</p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div 
                      key={message.id} 
                      className={`flex ${
                        message.sender_id === user.id ? 'justify-end' : 'justify-start'
                      }`}
                      data-testid={`message-${message.id}`}
                    >
                      <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        message.sender_id === user.id
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-white/10 text-foreground'
                      }`}>
                        <p className="text-sm">{message.content}</p>
                        <div className="flex items-center justify-end mt-1">
                          <Clock className="h-3 w-3 mr-1 opacity-60" />
                          <span className="text-xs opacity-60">
                            {formatTime(message.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-white/10">
                <div className="flex space-x-2">
                  <Input
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    className="flex-1"
                    data-testid="message-input"
                  />
                  <Button 
                    onClick={handleSendMessage}
                    disabled={sending || !newMessage.trim()}
                    className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))]"
                    data-testid="send-message-btn"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export default MessagesPage;
